import { useState, useCallback, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { getAuthHeaders, logout } from '../lib/auth';
import { Textarea } from '../components/ui/textarea';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BookMarked,
  Copy,
  Download,
  CheckCircle,
  AlertCircle,
  Loader2,
  LogOut,
  User,
  LayoutDashboard,
  FileText,
  FileUp,
  Printer,
  FileType2,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  BorderStyle,
  AlignmentType,
  convertInchesToTwip,
} from 'docx';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';

GlobalWorkerOptions.workerSrc =
  'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function wordCount(text) {
  return text.trim() ? text.trim().split(/\s+/).length : 0;
}

const escHtml = (s) =>
  (s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br>');

export default function HomeGroupGuidePage() {
  const navigate = useNavigate();

  // Form state
  const [transcript, setTranscript] = useState('');
  const [sermonTitle, setSermonTitle] = useState('');
  const [sermonDate, setSermonDate] = useState('');
  const [churchName, setChurchName] = useState('The Anchor Church');
  const [coverage, setCoverage] = useState('full');
  const [customSection, setCustomSection] = useState('');

  // UI state
  const [isGenerating, setIsGenerating] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [guide, setGuide] = useState(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState('');
  const [genProgress, setGenProgress] = useState(0);
  const [genLabel, setGenLabel] = useState('');
  const [usage, setUsage] = useState(null);
  const [limitHit, setLimitHit] = useState(null);

  const dropRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
      .then(r => r.ok ? r.json() : null)
      .then(d => { if (d) setUsage(d.usage?.home_group_guide); })
      .catch(() => {});
  }, []);

  const words = wordCount(transcript);
  const tooShort = words > 0 && words < 200;

  const handleLogout = () => logout(navigate);

  const startProgress = () => {
    const steps = [
      { at: 0,  label: 'Reading transcript…' },
      { at: 18, label: 'Analysing themes…' },
      { at: 42, label: 'Writing questions…' },
      { at: 72, label: 'Polishing guide…' },
    ];
    let val = 0;
    setGenProgress(0);
    setGenLabel(steps[0].label);
    progressRef.current = setInterval(() => {
      val += 0.7;
      const step = [...steps].reverse().find(s => val >= s.at);
      if (step) setGenLabel(step.label);
      setGenProgress(Math.min(val, 91));
      if (val >= 91) clearInterval(progressRef.current);
    }, 150);
  };

  const stopProgress = (success) => {
    clearInterval(progressRef.current);
    if (success) {
      setGenProgress(100);
      setGenLabel('Done!');
      setTimeout(() => { setGenProgress(0); setGenLabel(''); }, 700);
    } else {
      setGenProgress(0);
      setGenLabel('');
    }
  };

  // ── File extraction ──────────────────────────────────────────────────────
  const extractTextFromFile = async (file) => {
    setError('');
    setExtracting(true);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const isDocx =
        file.name?.endsWith('.docx') ||
        file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
      const isPdf = file.type === 'application/pdf' || file.name?.endsWith('.pdf');

      if (isDocx) {
        const result = await mammoth.extractRawText({ arrayBuffer });
        setTranscript(result.value.trim());
      } else if (isPdf) {
        const pdf = await getDocument({ data: arrayBuffer }).promise;
        let text = '';
        for (let i = 1; i <= pdf.numPages; i++) {
          const page = await pdf.getPage(i);
          const content = await page.getTextContent();
          text += content.items.map((item) => item.str).join(' ') + '\n';
        }
        setTranscript(text.trim());
      } else {
        setError('Unsupported file type. Please drop a .docx or .pdf file.');
      }
    } catch {
      setError('Could not extract text from the file. Please copy and paste the text manually.');
    } finally {
      setExtracting(false);
      setIsDragging(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) extractTextFromFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    if (!dropRef.current?.contains(e.relatedTarget)) setIsDragging(false);
  };

  // ── Generate ─────────────────────────────────────────────────────────────
  const handleGenerate = async (e) => {
    e.preventDefault();
    setError('');
    setLimitHit(null);
    if (!transcript.trim() || words < 200) return;

    setIsGenerating(true);
    setGuide(null);
    startProgress();

    try {
      const response = await fetch(`${API}/guides/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({
          transcript,
          sermon_title: sermonTitle,
          date: sermonDate,
          church_name: churchName,
          coverage,
          custom_section: customSection,
        }),
      });

      if (response.ok) {
        stopProgress(true);
        setGuide(await response.json());
        // Refresh usage count
        fetch(`${API}/usage`, { headers: { ...getAuthHeaders() } })
          .then(r => r.ok ? r.json() : null)
          .then(d => { if (d) setUsage(d.usage?.home_group_guide); })
          .catch(() => {});
      } else {
        stopProgress(false);
        const err = await response.json();
        if (response.status === 429) {
          setLimitHit(err.detail);
        } else {
          setError(err.detail || 'Failed to generate guide. Please try again.');
        }
      }
    } catch {
      stopProgress(false);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // ── Copy to clipboard ─────────────────────────────────────────────────────
  const buildPlainText = useCallback(() => {
    if (!guide) return '';
    const lines = [
      'HOME GROUP DISCUSSION GUIDE',
      guide.sermon_title && `Sermon: ${guide.sermon_title}`,
      guide.date && `Date: ${guide.date}`,
      guide.church_name && `Church: ${guide.church_name}`,
      '',
      'FOR THE LEADER:',
      guide.leader_note,
      '',
      'DISCUSSION QUESTIONS:',
      ...(guide.questions || []).flatMap((q, i) => [
        `${i + 1}. ${q.question}`,
        `   Follow-up: ${q.followup}`,
        `   Prayer: ${q.prayer}`,
        '',
      ]),
      guide.key_scripture?.reference && 'KEY SCRIPTURE:',
      guide.key_scripture?.reference &&
        `${guide.key_scripture.reference} — "${guide.key_scripture.text}"`,
      '',
      guide.closing_encouragement && 'CLOSING ENCOURAGEMENT:',
      guide.closing_encouragement,
    ]
      .filter(Boolean)
      .join('\n');
    return lines;
  }, [guide]);

  const handleCopy = async () => {
    const text = buildPlainText();
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  // ── Shared document HTML builder (used by PDF and Google Docs) ───────────
  const buildDocHtml = useCallback((forPrint = false) => {
    if (!guide) return '';
    const metaParts = [guide.date, guide.church_name].filter(Boolean).join('  ·  ');
    const questionsHtml = (guide.questions || []).map((q, i) => `
      <div class="q">
        <p class="q-num">${i + 1}.</p>
        <div class="q-body">
          <p class="q-text">${escHtml(q.question)}</p>
          ${q.followup ? `<p class="q-follow">${escHtml(q.followup)}</p>` : ''}
          ${q.prayer ? `<p class="q-prayer"><span class="prayer-label">Prayer: </span>${escHtml(q.prayer)}</p>` : ''}
        </div>
      </div>`).join('');

    return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <title>${escHtml(guide.sermon_title || 'Home Group Guide')}</title>
  <style>
    *{box-sizing:border-box;margin:0;padding:0}
    @page{size:letter;margin:1in 0.85in}
    body{font-family:Georgia,'Times New Roman',serif;color:#1a1a1a;font-size:11.5pt;line-height:1.75;background:#fff;-webkit-print-color-adjust:exact;print-color-adjust:exact}

    /* ── Header ── */
    .doc-header{background:#1B2A4A;padding:22pt 0.85in 18pt;margin:-1in -0.85in 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .doc-header .eyebrow{font-family:Arial,Helvetica,sans-serif;font-size:7pt;letter-spacing:.18em;text-transform:uppercase;color:rgba(255,255,255,.5);margin-bottom:5pt}
    .doc-header h1{font-size:22pt;font-weight:700;color:#ffffff;line-height:1.2;margin:0}
    .doc-header .meta{font-family:Arial,Helvetica,sans-serif;font-size:9pt;color:rgba(255,255,255,.6);margin-top:5pt}

    /* ── Body ── */
    .doc-body{padding-top:22pt}

    /* Leader note */
    .leader{border-left:3.5pt solid #C9A84C;padding:9pt 13pt 9pt 13pt;background:#fdf8f0;margin-bottom:22pt;border-radius:0 3pt 3pt 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .leader .lbl{font-family:Arial,Helvetica,sans-serif;font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:.14em;color:#C9A84C;margin-bottom:4pt}
    .leader p{font-style:italic;color:#4a4a4a;font-size:10.5pt;line-height:1.7}

    /* Section headings */
    .section-hd{font-family:Arial,Helvetica,sans-serif;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.16em;color:#1B2A4A;border-bottom:1.5pt solid #1B2A4A;padding-bottom:3.5pt;margin:0 0 14pt}

    /* Questions */
    .q{display:flex;gap:10pt;margin-bottom:17pt;page-break-inside:avoid}
    .q-num{font-family:Arial,Helvetica,sans-serif;font-weight:700;color:#1B2A4A;font-size:11pt;min-width:18pt;padding-top:1pt}
    .q-body{flex:1}
    .q-text{font-weight:700;color:#1B2A4A;font-size:11.5pt;margin-bottom:4pt}
    .q-follow{color:#4a4a4a;font-size:10.5pt;margin-bottom:3pt}
    .q-prayer{font-style:italic;color:#C9A84C;font-size:10pt}
    .prayer-label{font-weight:700;font-style:normal}

    /* Scripture */
    .scripture{background:#f0f4f8;border-left:3.5pt solid #1B2A4A;padding:13pt 15pt;margin:22pt 0;page-break-inside:avoid;border-radius:0 3pt 3pt 0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
    .scripture .lbl{font-family:Arial,Helvetica,sans-serif;font-size:7pt;font-weight:700;text-transform:uppercase;letter-spacing:.14em;color:#1B2A4A;margin-bottom:7pt}
    .scripture blockquote{font-style:italic;color:#2a2a2a;font-size:11.5pt;line-height:1.7}
    .scripture cite{display:block;font-family:Arial,Helvetica,sans-serif;font-size:8.5pt;color:#888;margin-top:6pt;font-style:normal}

    /* Closing */
    .closing-hd{font-family:Arial,Helvetica,sans-serif;font-size:7.5pt;font-weight:700;text-transform:uppercase;letter-spacing:.16em;color:#1B2A4A;border-bottom:1.5pt solid #1B2A4A;padding-bottom:3.5pt;margin:22pt 0 12pt}
    .closing{font-style:italic;color:#4a4a4a;font-size:11pt;line-height:1.75}

    /* Footer */
    .doc-footer{margin-top:28pt;padding-top:9pt;border-top:1pt solid #ddd;text-align:center;font-family:Arial,Helvetica,sans-serif;font-size:7.5pt;color:#bbb;letter-spacing:.06em}

    ${forPrint ? '' : '/* Screen preview */ body{padding:1in 0.85in} .doc-header{margin:-1in -0.85in 0}'}
  </style>
</head>
<body>
<div class="doc-header">
  <div class="eyebrow">Home Group Discussion Guide</div>
  <h1>${escHtml(guide.sermon_title || 'Discussion Guide')}</h1>
  ${metaParts ? `<div class="meta">${escHtml(metaParts)}</div>` : ''}
</div>
<div class="doc-body">
  ${guide.leader_note ? `<div class="leader"><div class="lbl">For the Leader</div><p>${escHtml(guide.leader_note)}</p></div>` : ''}
  ${(guide.questions || []).length ? `<div class="section-hd">Discussion Questions</div>${questionsHtml}` : ''}
  ${guide.key_scripture?.reference ? `
  <div class="scripture">
    <div class="lbl">Key Scripture</div>
    <blockquote>\u201c${escHtml(guide.key_scripture.text)}\u201d</blockquote>
    <cite>\u2014 ${escHtml(guide.key_scripture.reference)}</cite>
  </div>` : ''}
  ${guide.closing_encouragement ? `<div class="closing-hd">Closing Encouragement</div><p class="closing">${escHtml(guide.closing_encouragement)}</p>` : ''}
  <div class="doc-footer">Generated by SermonSift &nbsp;&middot;&nbsp; sermonsift.com</div>
</div>
</body>
</html>`;
  }, [guide]);

  // ── Download DOCX ─────────────────────────────────────────────────────────
  const handleDownloadDocx = async () => {
    if (!guide) return;
    const NAVY = '1B2A4A';
    const GOLD = 'C9A84C';
    const LIGHT = 'F0F4F8';
    const WARM = 'FDF8F0';
    const T = convertInchesToTwip;
    const children = [];

    // ─ Navy header ─
    children.push(new Paragraph({
      spacing: { before: 0, after: 0 },
      shading: { fill: NAVY },
      children: [new TextRun({ text: 'HOME GROUP DISCUSSION GUIDE', bold: true, color: 'FFFFFF', size: 32, font: 'Arial', allCaps: true })],
    }));
    children.push(new Paragraph({
      spacing: { after: 40 },
      shading: { fill: NAVY },
      children: [new TextRun({ text: [guide.sermon_title, guide.date, guide.church_name].filter(Boolean).join('   ·   ') || ' ', color: 'AABBCC', size: 20, font: 'Arial' })],
    }));
    children.push(new Paragraph({ spacing: { after: 240 }, children: [new TextRun({ text: '' })] }));

    // ─ Leader note ─
    if (guide.leader_note) {
      children.push(new Paragraph({
        spacing: { before: 0, after: 60 },
        border: { left: { style: BorderStyle.THICK, size: 28, color: GOLD } },
        shading: { fill: WARM },
        indent: { left: T(0.18) },
        children: [new TextRun({ text: 'FOR THE LEADER', bold: true, color: GOLD, size: 17, font: 'Arial', allCaps: true })],
      }));
      children.push(new Paragraph({
        spacing: { after: 320 },
        border: { left: { style: BorderStyle.THICK, size: 28, color: GOLD } },
        shading: { fill: WARM },
        indent: { left: T(0.18) },
        children: [new TextRun({ text: guide.leader_note, italics: true, size: 22, font: 'Georgia', color: '444444' })],
      }));
    }

    // ─ Questions heading ─
    children.push(new Paragraph({
      spacing: { before: 0, after: 180 },
      border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: NAVY } },
      children: [new TextRun({ text: 'DISCUSSION QUESTIONS', bold: true, color: NAVY, size: 18, font: 'Arial', allCaps: true })],
    }));

    // ─ Questions ─
    (guide.questions || []).forEach((q, i) => {
      children.push(new Paragraph({
        spacing: { before: 220, after: 80 },
        indent: { hanging: T(0.22), left: T(0.22) },
        children: [
          new TextRun({ text: `${i + 1}.  `, bold: true, color: NAVY, size: 23, font: 'Arial' }),
          new TextRun({ text: q.question, bold: true, color: NAVY, size: 23, font: 'Georgia' }),
        ],
      }));
      if (q.followup) children.push(new Paragraph({
        spacing: { after: 60 },
        indent: { left: T(0.35) },
        children: [new TextRun({ text: q.followup, size: 21, font: 'Georgia', color: '444444' })],
      }));
      if (q.prayer) children.push(new Paragraph({
        spacing: { after: 60 },
        indent: { left: T(0.35) },
        children: [
          new TextRun({ text: 'Prayer: ', bold: true, italics: true, color: GOLD, size: 19, font: 'Arial' }),
          new TextRun({ text: q.prayer, italics: true, color: GOLD, size: 19, font: 'Georgia' }),
        ],
      }));
    });

    // ─ Key Scripture ─
    if (guide.key_scripture?.reference) {
      children.push(new Paragraph({ spacing: { before: 320, after: 0 }, children: [new TextRun({ text: '' })] }));
      children.push(new Paragraph({
        spacing: { before: 0, after: 120 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: NAVY } },
        children: [new TextRun({ text: 'KEY SCRIPTURE', bold: true, color: NAVY, size: 18, font: 'Arial', allCaps: true })],
      }));
      children.push(new Paragraph({
        spacing: { before: 140, after: 60 },
        shading: { fill: LIGHT },
        indent: { left: T(0.25), right: T(0.25) },
        children: [new TextRun({ text: `\u201c${guide.key_scripture.text}\u201d`, italics: true, size: 22, font: 'Georgia', color: '2a2a2a' })],
      }));
      children.push(new Paragraph({
        spacing: { after: 320 },
        shading: { fill: LIGHT },
        indent: { left: T(0.25) },
        children: [new TextRun({ text: `\u2014 ${guide.key_scripture.reference}`, bold: true, size: 19, font: 'Arial', color: '666666' })],
      }));
    }

    // ─ Closing ─
    if (guide.closing_encouragement) {
      children.push(new Paragraph({
        spacing: { before: 0, after: 100 },
        border: { bottom: { style: BorderStyle.SINGLE, size: 8, color: NAVY } },
        children: [new TextRun({ text: 'CLOSING ENCOURAGEMENT', bold: true, color: NAVY, size: 18, font: 'Arial', allCaps: true })],
      }));
      children.push(new Paragraph({
        spacing: { before: 140, after: 280 },
        children: [new TextRun({ text: guide.closing_encouragement, italics: true, size: 22, font: 'Georgia', color: '444444' })],
      }));
    }

    // ─ Footer ─
    children.push(new Paragraph({
      alignment: AlignmentType.CENTER,
      spacing: { before: 200 },
      border: { top: { style: BorderStyle.SINGLE, size: 4, color: 'DDDDDD' } },
      children: [new TextRun({ text: 'Generated by SermonSift  ·  sermonsift.com', size: 15, color: 'BBBBBB', font: 'Arial' })],
    }));

    const doc = new Document({
      sections: [{
        properties: {
          page: {
            margin: {
              top:    convertInchesToTwip(1),
              bottom: convertInchesToTwip(1),
              left:   convertInchesToTwip(0.85),
              right:  convertInchesToTwip(0.85),
            },
          },
        },
        children,
      }],
    });

    const blob = await Packer.toBlob(doc);
    const safeTitle = (guide.sermon_title || 'home-group-guide').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    saveAs(blob, `${safeTitle}.docx`);
  };

  // ── Print / Save as PDF ───────────────────────────────────────────────────
  const handlePrintPDF = () => {
    if (!guide) return;
    const w = window.open('', '_blank', 'width=880,height=1120');
    w.document.write(buildDocHtml(true));
    w.document.close();
    w.focus();
    setTimeout(() => w.print(), 700);
  };

  // ── Download as Google Docs–compatible HTML ───────────────────────────────
  const handleDownloadGoogleDoc = () => {
    if (!guide) return;
    const html = buildDocHtml(false);
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const safeTitle = (guide.sermon_title || 'home-group-guide').replace(/[^a-z0-9]/gi, '-').toLowerCase();
    saveAs(blob, `${safeTitle}.html`);
    toast.success('Downloaded! Upload this file to Google Drive, then right-click → Open with Google Docs.', { duration: 6000 });
  };

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#fafaf9]">
      {/* Navbar */}
      <header className="sticky top-0 z-50 glass border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="font-serif text-xl font-bold text-[#1e3a8a]">SermonSift</Link>
            <Link to="/dashboard" className="hidden sm:flex items-center gap-2 text-sm text-stone-500 hover:text-[#1e3a8a]">
              <LayoutDashboard className="w-4 h-4" /> Dashboard
            </Link>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:flex items-center gap-2 text-sm font-medium text-[#1e3a8a]">
              <BookMarked className="w-4 h-4" /> Home Group Guide
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2 rounded-lg hover:bg-stone-100">
                  <User className="w-5 h-5 text-stone-600" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={handleLogout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Page header */}
        <div className="mb-6 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#1e3a8a]/10 flex items-center justify-center flex-shrink-0">
            <BookMarked className="w-4 h-4 text-[#1e3a8a]" strokeWidth={1.5} />
          </div>
          <div>
            <h1 className="text-xl font-serif font-bold text-[#1e3a8a] leading-none">Home Group Guide</h1>
            <p className="text-stone-500 text-xs mt-0.5">
              Paste or drop a transcript → generate a ready-to-use discussion guide
            </p>
          </div>
        </div>

        <div className="lg:grid lg:grid-cols-2 lg:gap-8 lg:items-start">

          {/* ── LEFT: Form ── */}
          <div>
            <form onSubmit={handleGenerate} className="space-y-4">

              {/* Transcript drop zone */}
              <div className="card p-4">
                <div className="flex items-center justify-between mb-1.5">
                  <Label className="text-sm font-medium text-stone-700">Sermon Transcript *</Label>
                  <span className={`text-xs tabular-nums ${tooShort ? 'text-amber-600' : 'text-stone-400'}`}>
                    {words.toLocaleString()} words
                  </span>
                </div>

                <div
                  ref={dropRef}
                  className="relative"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  {/* Drag overlay */}
                  {isDragging && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md border-2 border-dashed border-[#1e3a8a] bg-[#1e3a8a]/10 pointer-events-none">
                      <FileUp className="w-8 h-8 text-[#1e3a8a]" />
                      <p className="text-sm font-semibold text-[#1e3a8a]">Drop to extract text</p>
                      <p className="text-xs text-[#1e3a8a]/70">.docx or .pdf</p>
                    </div>
                  )}

                  {/* Extracting overlay */}
                  {extracting && (
                    <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-2 rounded-md bg-white/80 backdrop-blur-sm">
                      <Loader2 className="w-6 h-6 text-[#1e3a8a] animate-spin" />
                      <p className="text-sm text-[#1e3a8a]">Extracting text…</p>
                    </div>
                  )}

                  <Textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Paste the full sermon transcript here…"
                    className="min-h-[220px] resize-y text-sm"
                    required
                  />
                </div>

                <p className="mt-1.5 text-xs text-stone-400 flex items-center gap-1">
                  <FileUp className="w-3 h-3" />
                  Drag a <strong>.docx</strong> or <strong>.pdf</strong> file above to extract text automatically
                </p>

                {tooShort && (
                  <div className="mt-2 flex items-start gap-2 text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <p className="text-xs">Transcript is too short ({words} words). Aim for at least 200 words.</p>
                  </div>
                )}
              </div>

              {/* Sermon details */}
              <div className="card p-4">
                <p className="text-xs font-semibold text-stone-400 uppercase tracking-wide mb-3">Sermon Details (optional)</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Sermon Title</Label>
                    <Input value={sermonTitle} onChange={(e) => setSermonTitle(e.target.value)} placeholder="e.g. Walking in Faith" className="h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Date</Label>
                    <Input value={sermonDate} onChange={(e) => setSermonDate(e.target.value)} placeholder="e.g. March 2, 2026" className="h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Church Name</Label>
                    <Input value={churchName} onChange={(e) => setChurchName(e.target.value)} placeholder="The Anchor Church" className="h-9 text-sm" />
                  </div>
                  <div>
                    <Label className="text-xs text-stone-600 mb-1 block">Sermon Coverage</Label>
                    <Select value={coverage} onValueChange={setCoverage}>
                      <SelectTrigger className="h-9 text-sm">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Full Sermon</SelectItem>
                        <SelectItem value="part1">Part 1 Only</SelectItem>
                        <SelectItem value="custom">Custom Section</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                {coverage === 'custom' && (
                  <div className="mt-3">
                    <Label className="text-xs text-stone-600 mb-1 block">Describe the section to focus on</Label>
                    <Input value={customSection} onChange={(e) => setCustomSection(e.target.value)} placeholder="e.g. The section on forgiveness, starting after the story about…" className="text-sm" />
                  </div>
                )}
              </div>

              {error && (
                <div className="flex items-start gap-2 text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <p className="text-sm">{error}</p>
                </div>
              )}

              <button
                type="submit"
                className="btn-primary w-full flex items-center justify-center gap-2"
                disabled={isGenerating || !transcript.trim() || tooShort}
              >
                {isGenerating ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Generating guide…</>
                ) : (
                  <><BookMarked className="w-4 h-4" /> Generate Guide</>
                )}
              </button>

              {/* Usage badge */}
              {usage && (
                <div className="flex items-center gap-1.5 text-xs mt-2 justify-end">
                  {usage.unlimited
                    ? <span className="text-stone-400">Unlimited</span>
                    : <span className={usage.remaining === 0 ? 'text-red-500' : usage.remaining <= 2 ? 'text-amber-500' : 'text-stone-400'}>
                        {usage.remaining} of {usage.limit} uses left this month
                      </span>
                  }
                </div>
              )}

              {/* Limit alert */}
              {limitHit && (
                <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-4 text-center">
                  <p className="text-sm font-semibold text-red-800 mb-1">Monthly limit reached</p>
                  <p className="text-xs text-red-700 mb-3">{limitHit}</p>
                  <Link to="/pricing">
                    <button type="button" className="text-xs font-semibold px-4 py-2 rounded-lg bg-[#1e3a8a] text-white">
                      Upgrade Plan
                    </button>
                  </Link>
                </div>
              )}

              {isGenerating && (
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs text-stone-500">{genLabel}</span>
                    <span className="text-xs font-bold tabular-nums text-[#1e3a8a]">{Math.round(genProgress)}%</span>
                  </div>
                  <div className="h-1.5 rounded-full overflow-hidden bg-stone-200">
                    <div
                      className="h-full rounded-full transition-all duration-300 ease-out"
                      style={{ width: `${genProgress}%`, background: 'linear-gradient(90deg, #1e3a8a, #3b5fc4)' }}
                    />
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* ── RIGHT: Document Preview ── */}
          <div className="mt-6 lg:mt-0 lg:sticky lg:top-20">
            <div className="rounded-xl overflow-hidden border border-stone-200 shadow-sm">

              {/* Preview toolbar */}
              <div className="bg-stone-100 border-b border-stone-200 px-4 py-2.5 flex items-center justify-between">
                <span className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Document Preview</span>
                {guide && (
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <button
                      onClick={handleCopy}
                      className="flex items-center gap-1.5 text-xs text-stone-600 hover:text-[#1e3a8a] px-2.5 py-1.5 rounded-md hover:bg-white transition-colors"
                    >
                      {copied ? <CheckCircle className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                      {copied ? 'Copied!' : 'Copy'}
                    </button>
                    <button
                      onClick={handleDownloadDocx}
                      className="flex items-center gap-1.5 text-xs text-stone-600 hover:text-[#1e3a8a] px-2.5 py-1.5 rounded-md hover:bg-white transition-colors"
                    >
                      <Download className="w-3.5 h-3.5" /> Word
                    </button>
                    <button
                      onClick={handleDownloadGoogleDoc}
                      title="Download as HTML — upload to Google Drive and open as Google Doc"
                      className="flex items-center gap-1.5 text-xs text-stone-600 hover:text-[#1e3a8a] px-2.5 py-1.5 rounded-md hover:bg-white transition-colors"
                    >
                      <FileType2 className="w-3.5 h-3.5" /> Google Docs
                    </button>
                    <button
                      onClick={handlePrintPDF}
                      className="flex items-center gap-1.5 text-xs bg-[#1e3a8a] text-white px-2.5 py-1.5 rounded-md hover:bg-[#172554] transition-colors"
                    >
                      <Printer className="w-3.5 h-3.5" /> Save PDF
                    </button>
                  </div>
                )}
              </div>

              {/* Paper area */}
              <div className="bg-[#d8d8d8] p-5 min-h-[600px] max-h-[80vh] overflow-y-auto">
                {!guide ? (
                  <div className="h-full min-h-[500px] flex flex-col items-center justify-center text-stone-400 gap-3">
                    <FileText className="w-12 h-12 opacity-25" />
                    <p className="text-sm font-medium">Your guide will appear here</p>
                    <p className="text-xs text-stone-400">Fill in the form and click Generate Guide</p>
                  </div>
                ) : (
                  <div className="bg-white shadow-[0_4px_24px_rgba(0,0,0,0.18)] rounded-sm max-w-[640px] mx-auto overflow-hidden text-sm leading-relaxed" data-testid="guide-output">

                    {/* Document navy header */}
                    <div className="bg-[#1B2A4A] px-7 py-6">
                      <p className="text-[10px] font-sans font-bold uppercase tracking-[0.18em] text-white/50 mb-1.5">
                        Home Group Discussion Guide
                      </p>
                      <h2 className="text-white font-serif text-lg font-bold leading-snug">
                        {guide.sermon_title || 'Discussion Guide'}
                      </h2>
                      {(guide.date || guide.church_name) && (
                        <p className="text-white/60 text-xs font-sans mt-1.5">
                          {[guide.date, guide.church_name].filter(Boolean).join('  ·  ')}
                        </p>
                      )}
                    </div>

                    {/* Document body */}
                    <div className="px-7 py-6 space-y-5">

                      {/* Leader note */}
                      {guide.leader_note && (
                        <div className="border-l-4 border-[#C9A84C] pl-4 py-1 bg-[#fdf8f0] rounded-r">
                          <p className="text-[9px] font-sans font-bold uppercase tracking-[0.14em] text-[#C9A84C] mb-1.5">
                            For the Leader
                          </p>
                          <p className="italic text-stone-600 text-[11.5px] leading-relaxed">
                            {guide.leader_note}
                          </p>
                        </div>
                      )}

                      {/* Questions */}
                      {(guide.questions || []).length > 0 && (
                        <div>
                          <p className="text-[9px] font-sans font-bold uppercase tracking-[0.14em] text-[#1B2A4A] border-b border-[#1B2A4A] pb-1.5 mb-3">
                            Discussion Questions
                          </p>
                          <div className="space-y-4">
                            {guide.questions.map((q, i) => (
                              <div key={i}>
                                <p className="font-bold text-[#1B2A4A] font-serif text-[11.5px]">
                                  {i + 1}.&nbsp; {q.question}
                                </p>
                                {q.followup && (
                                  <p className="text-stone-600 text-[10.5px] mt-1 ml-5">{q.followup}</p>
                                )}
                                {q.prayer && (
                                  <p className="italic text-[#C9A84C] text-[10px] mt-1 ml-5">
                                    <span className="font-bold not-italic">Prayer:</span> {q.prayer}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Key Scripture */}
                      {guide.key_scripture?.reference && (
                        <div className="bg-[#f0f4f8] border border-[#1B2A4A]/15 border-l-4 border-l-[#1B2A4A] rounded-r px-4 py-3">
                          <p className="text-[9px] font-sans font-bold uppercase tracking-[0.14em] text-[#1B2A4A] mb-2">
                            Key Scripture
                          </p>
                          <p className="italic text-stone-700 font-serif text-[11px] leading-relaxed">
                            &ldquo;{guide.key_scripture.text}&rdquo;
                          </p>
                          <p className="text-[9.5px] text-stone-500 font-sans mt-1.5">
                            &mdash; {guide.key_scripture.reference}
                          </p>
                        </div>
                      )}

                      {/* Closing */}
                      {guide.closing_encouragement && (
                        <p className="italic text-stone-600 text-[11px] leading-relaxed pt-1 border-t border-stone-100">
                          {guide.closing_encouragement}
                        </p>
                      )}

                      {/* Footer */}
                      <p className="text-center text-[8.5px] font-sans text-stone-300 pt-3 border-t border-stone-100 tracking-wide">
                        Generated by SermonSift · sermonsift.com
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </main>
    </div>
  );
}
